import { useEffect, useState, useReducer, useRef } from "react";
import CentralizedModalContainer from "@/appComponents/Blocks/Utils/CentralizedModalContainer/CentralizedModalContainer";
import {
  Alert,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  Modal,
  Flex,
  Divider,
  Text,
  SearchMenu,
  List,
  Input
} from "@theorgchartdev/storybook";
import cx from "classnames";
import { appstr } from "@coreapp/utils/appstr";
import { enumShareRoles } from "@coreapp/constants";
import { ShareWithHandler, SharingMode } from "../PlanBuilderModal/SharedWithHandler";
import { GetWSMgr, GetDoc } from "@/Utils/StateLib";
import { QSharedFilterRFC } from "@coreapp/CoreApp/QSharedFilterRFC";
import { TOAST_STATUS, useToast } from "@/providers/ToastProvider";
import { createBoldToastMessage } from "@/appComponents/Blocks/Cards/ToastContainer/utils";
import { ReadOnlyCriteriaList } from "@/appComponents/Navigation/RightSidebar/Utils/ReadOnlyCriteriaList";
import { QRulePair } from "@coreapp/CoreApp/QRulePair";
import { chartModeTestId } from "@/appComponents/Navigation/RightSidebar/Utils/chartModeTestId";
import "./ShareFilterModal.css";

type ShareFilterModalProps = {
  show: boolean;
  onClose: () => void;
  filterType: "filter" | "highlight";
  definition: any;
  filterName: string;
  filterRef?: any;
  onShareComplete?: (filterId: string) => void;
  editMode?: boolean;
  existingFilterId?: string;
  existingSharedWith?: Array<{
    type: "user" | "group";
    id: string;
    displayName: string;
    role: string;
    sharedAt?: string;
    sharedBy?: string;
  }>;
};

type ShareFormState = {
  sharedWith: any[];
  filterName: string;
};

type ShareFormAction =
  | { type: "SET_SHARED_WITH"; payload: any[] }
  | { type: "RESET_SHARED_WITH" }
  | { type: "REMOVE_SHARED_WITH"; payload: string }
  | { type: "SET_SHARED_WITH_ROLE"; payload: { username: string; role: string } }
  | { type: "SET_FILTER_NAME"; payload: string };

const shareFormReducer = (state: ShareFormState, action: ShareFormAction): ShareFormState => {
  switch (action.type) {
    case "SET_SHARED_WITH": {
      const sharedWith = [...state.sharedWith];
      const sharedWithUsernames = sharedWith.map((user: any) => user.username);

      action.payload.forEach((user: any) => {
        const existingUserIndex = sharedWithUsernames.indexOf(user.username);
        if (existingUserIndex === -1) {
          if (!user.editorRole) {
            user.editorRole = enumShareRoles.collaborator;
          }
          sharedWith.push(user);
        } else {
          if (user.editorRole) {
            sharedWith[existingUserIndex].editorRole = user.editorRole;
          }
        }
      });

      return { ...state, sharedWith };
    }
    case "RESET_SHARED_WITH":
      return { ...state, sharedWith: [] };
    case "REMOVE_SHARED_WITH":
      return {
        ...state,
        sharedWith: state.sharedWith.filter((user: any) => user.username !== action.payload)
      };
    case "SET_SHARED_WITH_ROLE":
      return {
        ...state,
        sharedWith: state.sharedWith.map((user: any) => {
          if (user.username === action.payload.username) {
            user.editorRole = action.payload.role;
          }
          return user;
        })
      };
    case "SET_FILTER_NAME":
      return { ...state, filterName: action.payload };
    default:
      return state;
  }
};

const getRulesFromRef = (filterRef: any, filterType: string): QRulePair[] => {
  if (!filterRef) return [];

  if (filterType === "filter") {
    return filterRef.GetRules?.() ?? filterRef.fFilterRuleList ?? [];
  }

  // Highlight: fDef is a QRule with fRulePairList
  const def = filterRef.fDef ?? filterRef.GetDef?.();
  return def?.GetRulePairList?.() ?? def?.fRulePairList ?? [];
};

export const ShareFilterModal = (props: ShareFilterModalProps) => {
  const {
    show,
    onClose,
    filterType,
    definition,
    filterName,
    filterRef,
    onShareComplete,
    editMode,
    existingFilterId,
    existingSharedWith
  } = props;
  const toastProv: any = useToast();
  const addToast = toastProv?.addToast;

  const [shareFormState, dispatch] = useReducer(shareFormReducer, {
    sharedWith: [],
    filterName: filterName
  });

  const [shareQuery, setShareQuery] = useState("");
  const [sharedWithHandler, setSharedWithHandler] = useState<ShareWithHandler | null>(null);
  const [duplicateWarning, setDuplicateWarning] = useState("");
  const [saving, setSaving] = useState(false);

  const searchCtrlRef = useRef(null);

  useEffect(() => {
    if (!show) {
      dispatch({ type: "RESET_SHARED_WITH" });
      dispatch({ type: "SET_FILTER_NAME", payload: filterName });
      setDuplicateWarning("");
      return;
    }

    dispatch({ type: "SET_FILTER_NAME", payload: filterName });

    const asyncHandler = async () => {
      const shareHandler = new ShareWithHandler(SharingMode.filter);
      await shareHandler.loadUsersAndGroups();

      if (editMode && existingSharedWith?.length) {
        const prePopulated = existingSharedWith.map(entry => ({
          username: entry.id,
          name: entry.displayName,
          editorRole: entry.role,
          isGroup: entry.type === "group"
        }));
        dispatch({ type: "SET_SHARED_WITH", payload: prePopulated });
      }

      shareHandler.buildUserAndGroupOptions([], (users: any[]) => {
        dispatch({ type: "SET_SHARED_WITH", payload: users });
      });

      setSharedWithHandler(shareHandler);
    };

    asyncHandler();
  }, [show, filterName, editMode, existingSharedWith]);

  if (!show) return null;

  const wsMgr = GetWSMgr();
  const ownerDisplayName = wsMgr?.GetUserInfo()?.GetName?.() || wsMgr?.GetUserName() || "";
  const rules = getRulesFromRef(filterRef, filterType);
  const entityLabel = appstr(filterType === "filter" ? "Filter" : "Highlight");

  const handleShare = async () => {
    const doc = GetDoc();
    if (!wsMgr || !doc) return;

    const chartId = doc.GetChartID();
    if (!chartId) return;

    setSaving(true);

    const sharedWithData = shareFormState.sharedWith
      .filter((user: any) => !user.isGroup)
      .map((user: any) => ({
        type: "user" as const,
        id: user.username,
        displayName: user.name || user.username,
        role: user.editorRole || enumShareRoles.viewer
      }));

    try {
      let raw;
      if (editMode && existingFilterId) {
        raw = await QSharedFilterRFC.UpdateShareAccess(wsMgr, existingFilterId, sharedWithData);
      } else {
        const data = {
          chartId: chartId,
          type: filterType,
          definition: {
            ...definition,
            name: shareFormState.filterName
          },
          sharedWith: sharedWithData,
          ownerDisplayName: ownerDisplayName
        };
        raw = await QSharedFilterRFC.ShareFilter(wsMgr, data);
      }

      const response = typeof raw === "string" ? JSON.parse(raw) : raw;

      if (response?.success) {
        addToast?.({
          ...createBoldToastMessage("Entity sharing access saved", {
            entity: shareFormState.filterName
          }),
          status: TOAST_STATUS.SUCCESS,
          timeoutMS: 5000
        });
        onShareComplete?.(editMode ? existingFilterId! : response.filterId);
        wsMgr?.GetUsageTracker()?.ReportEvent("chart_mode.shared_filter.saved");
        onClose();
      } else {
        if (response?.code === "DUPLICATE_NAME") {
          setDuplicateWarning(
            appstr(
              filterType === "filter"
                ? "A shared filter with this name already exists"
                : "A shared highlight with this name already exists"
            )
          );
        }
        addToast?.({
          message: response?.message || appstr("Failed to share"),
          status: TOAST_STATUS.ERROR,
          timeoutMS: 5000
        });
      }
    } catch (err) {
      console.error("Failed to share filter:", err);
      addToast?.({
        message: appstr("Failed to share"),
        status: TOAST_STATUS.ERROR,
        timeoutMS: 5000
      });
    } finally {
      setSaving(false);
    }
  };

  const onInput = (inputValue: string) => {
    setShareQuery(inputValue);
  };

  const onResetInput = () => {
    setShareQuery("");
  };

  const shareWithOptions =
    sharedWithHandler?.getShareWithOptions(shareQuery || "", shareFormState.sharedWith) ?? [];

  const sharedUserList =
    sharedWithHandler?.buildActiveSharedUserOptions(
      shareFormState.sharedWith,
      (username: string, role: string) =>
        dispatch({ type: "SET_SHARED_WITH_ROLE", payload: { username, role } }),
      (username: string) => dispatch({ type: "REMOVE_SHARED_WITH", payload: username }),
      chartModeTestId(filterType, "share-role-trigger")
    ) ?? [];
  const shareDisabled =
    saving ||
    (editMode && !existingFilterId) ||
    (!editMode && shareFormState.sharedWith.length === 0);

  return (
    <CentralizedModalContainer show={show}>
      <Modal
        id="share-filter-modal"
        show={show}
        onClose={onClose}
        minWidth="medium"
        maxWidth="medium"
        withShadow={true}
        centerScreen={true}
      >
        <DialogTitle
          divider={true}
          onClose={onClose}
          iconProps={{
            name: "xmark",
            dataTestId: chartModeTestId(filterType, "share-modal-close-btn")
          }}
        >
          <span className="ocn-explorer-icon-label">
            <Icon size="medium" name="share-nodes" />
            {editMode
              ? appstr("Update sharing")
              : filterType === "filter"
                ? appstr("Share filter")
                : appstr("Share highlight")}
          </span>
        </DialogTitle>
        <DialogContent customContentClass="oc-modal-content">
          <Flex direction="column" gap="12px" height="100%">
            {duplicateWarning && (
              <Alert severity="warning" iconOverride="triangle-exclamation">
                {duplicateWarning}
              </Alert>
            )}
            <Text size="small">
              {appstr(
                filterType === "filter" ? "Share filter info text" : "Share highlight info text"
              )}
            </Text>
            <Input
              label={appstr("Entity name", { entity: entityLabel })}
              value={shareFormState.filterName}
              onInput={(e: any) => {
                setDuplicateWarning("");
                dispatch({ type: "SET_FILTER_NAME", payload: e.target.value });
              }}
              size="small"
              dataTestId="share-filter-name-input"
              disabled={editMode}
            />
            <Divider />
            <Text size="medium" modifier="medium">
              {appstr("People with access")}
            </Text>
            <Text size="small">
              {appstr("Share this entity with", { entity: entityLabel.toLowerCase() })}
            </Text>
            <SearchMenu
              placeholder={appstr("Add people or groups")}
              searchSize="small"
              searchProps={{
                onResetInput: onResetInput,
                dataTestId: "share-filter-search-input"
              }}
              options={shareWithOptions}
              optionSize="small"
              searchCallback={onInput}
              listWidth="large"
              loading={false}
              defaultInputVal=""
              openCtrlRef={searchCtrlRef}
            />
            <div className={cx("share-filter-people-list", "ocn-list-scrollbar")}>
              <List
                enableVirtualization={false}
                listWidth="fill"
                options={sharedUserList}
                showCheck={false}
              />
            </div>
            <Divider />
            <Text size="medium" modifier="medium">
              {appstr("Entity details", { entity: entityLabel })}
            </Text>
            <Flex gap="4px">
              <Text size="small" modifier="bold">
                {appstr("Owner")}:
              </Text>
              <Text size="small">{ownerDisplayName}</Text>
            </Flex>
            {rules.length > 0 && (
              <div className={cx("share-filter-criteria-container", "ocn-list-scrollbar")}>
                <ReadOnlyCriteriaList rules={rules} />
              </div>
            )}
          </Flex>
        </DialogContent>
        <DialogActions divider={true}>
          <DialogActions.Tertiary
            onClick={onClose}
            dataTestId={chartModeTestId(filterType, "share-modal-cancel-btn")}
          >
            {appstr("Cancel")}
          </DialogActions.Tertiary>
          <DialogActions.Primary
            onClick={handleShare}
            disabled={shareDisabled}
            dataTestId={chartModeTestId(
              filterType,
              editMode ? "share-modal-update-btn" : "share-modal-share-btn"
            )}
          >
            {editMode ? appstr("Update") : appstr("Share")}
          </DialogActions.Primary>
        </DialogActions>
      </Modal>
    </CentralizedModalContainer>
  );
};
